import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Route } from '~/Constants/Routes.constants';
import { routeReplacer } from '~/Helpers/String.helper';
import { useNavigation, useProvider } from '~/Hooks';
import { IChecklistListItem } from '~/ExportedComponents/TPD/Checklist/checklistListItem.model';
import { changeLocation } from '~/Helpers/Route.helpers';
import { externalUrls } from '~/Constants/Urls.constants';
import { useRequestChecklist } from '~/requests/providerService';
import { ChecklistStep } from '~/ExportedComponents/TPD/Checklist/types';

const { CUSTOMER_CREATE, EVENT_CREATE, PRODUCTS_CREATE } = Route;

export const useNormalChecklist = () => {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const {
    normalChecklist: checklist,
    updateNormalChecklist,
    isCheckListLoading,
    checklistError,
  } = useRequestChecklist();
  const { currentProvider } = useProvider();

  // CONSTANTS
  const domain = currentProvider?.domain || '';

  const activeIndex = useMemo(() => {
    if (checklist) {
      if (checklist.bookingToolShared) {
        return ChecklistStep.DONE;
      }
      if (checklist.customerCreated) {
        return ChecklistStep.BOOKING_TOOL_SHARED;
      }
      if (checklist.productCreated) {
        return ChecklistStep.CUSTOMER_CREATED;
      }
      if (checklist.eventCreated) {
        return ChecklistStep.PRODUCT_CREATED;
      }
      return ChecklistStep.EVENT_CREATED;
    }
    return ChecklistStep.NO_STEPS;
  }, [checklist]);

  const normalChecklistItems: IChecklistListItem[] = useMemo(
    () => [
      {
        index: 1,
        title: t('dashboard.checklist.normal.item1.title'),
        description: t('dashboard.checklist.normal.item1.description'),
        deactivated: !checklist?.eventCreated,
        active: activeIndex === ChecklistStep.EVENT_CREATED,
        onClick: () => {
          navigate(routeReplacer(EVENT_CREATE, { domain }));
        },
      },
      {
        index: 2,
        title: t('dashboard.checklist.normal.item2.title'),
        description: t('dashboard.checklist.normal.item2.description'),
        deactivated: !checklist?.productCreated,
        active: activeIndex === ChecklistStep.PRODUCT_CREATED,
        onClick: () => {
          navigate(routeReplacer(PRODUCTS_CREATE, { domain }));
        },
      },
      {
        index: 3,
        title: t('dashboard.checklist.normal.item3.title'),
        description: t('dashboard.checklist.normal.item3.description'),
        deactivated: !checklist?.customerCreated,
        active: activeIndex === ChecklistStep.CUSTOMER_CREATED,
        onClick: () => {
          navigate(routeReplacer(CUSTOMER_CREATE, { domain }));
        },
      },
      {
        index: 4,
        title: t('dashboard.checklist.normal.item4.title'),
        description: t('dashboard.checklist.normal.item4.description'),
        deactivated: !checklist?.bookingToolShared,
        active: activeIndex === ChecklistStep.BOOKING_TOOL_SHARED,
        onClick: () => {
          updateNormalChecklist({ bookingToolShared: new Date().toISOString() });
          changeLocation(`${externalUrls.BOOKING_TOOL_URL}/${domain}`);
        },
      },
    ],
    [activeIndex, checklist, domain, t, navigate, updateNormalChecklist],
  );

  const checklistFooter: IChecklistListItem = {
    index: 1,
    title: t('dashboard.checklist.normal.footer.title'),
    description: t('dashboard.checklist.normal.footer.description'),
  };

  return {
    activeIndex,
    normalChecklistItems,
    updateNormalChecklist,
    isCheckListLoading,
    checklistError,
    checklist,
    checklistFooter,
  };
};
